import React, { useId } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import ChartDefs from './ChartDefs';
import ChartTooltip from './ChartTooltip';
import useChartTheme from './useChartTheme';
import { makeChartIds } from './chartTokens';

/* ──────────────────────────────────────────────────────────────────────────
   GlowDonut — donut recipe (§3): per-segment NEON arcs, each with a glowing edge,
   dark gaps (paddingAngle), centre hole. NEON is the one sanctioned non-gold set.
   On-arc `name + %` labels are kept (theme-aware text colour).
   ────────────────────────────────────────────────────────────────────────── */

const RAD = Math.PI / 180;

const GlowDonut = ({
  data,
  dataKey,
  nameKey,
  height = 300,
  valueFormatter,
  showLabels = true,
  innerRadius = '58%',
  outerRadius = '80%',
}) => {
  const ids = makeChartIds(useId());
  const colors = useChartTheme();
  const neon = colors.neon;
  const colorByName = {};
  data.forEach((d, i) => { colorByName[d[nameKey]] = neon[i % neon.length]; });

  /* name + % just outside each arc, in muted ink so it reads in both themes. */
  const renderLabel = ({ cx, cy, midAngle, outerRadius: or, name, percent }) => {
    const r = or + 14;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text
        x={x}
        y={y}
        fill={colors.ink2}
        fontSize={11}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <>
      <ChartDefs ids={ids} colors={colors} />
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            stroke={colors.paper}
            strokeWidth={2}
            labelLine={false}
            label={showLabels ? renderLabel : false}
          >
            {data.map((entry, i) => {
              const color = neon[i % neon.length];
              return (
                <Cell
                  key={entry[nameKey] ?? i}
                  fill={color}
                  stroke={color}
                  strokeWidth={1}
                  filter={`url(#${ids.glow})`}
                />
              );
            })}
          </Pie>
          <Tooltip
            content={
              <ChartTooltip valueFormatter={valueFormatter} getColor={(p) => colorByName[p.name]} />
            }
          />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};

export default GlowDonut;
